<template>
    <section id="monitor-signs"
        class="monitor-signs"
    >
        <div class="table" 
            no-gutters
        >
            <div v-for="(item, index) in sort_data" 
                :key="index"
                class="table-item"
            >
                <v-tooltip v-if="enable_display_tooltip"
                    right 
                >
                    <template #activator="{ on }">
                        <div
                            class="table-item__key"
                            :class="{
                                'table-item__key--warning': item.signs_num === 3,
                                'table-item__key--alert': item.signs_num === 2,
                                'table-item__key--action': item.signs_num === 1
                            }"
                            v-on="on"
                        >
                            {{ item.idx }}
                        </div>
                    </template>
                    <span v-html="generate_tooltop_display_info(item)" />
                </v-tooltip>
                <div v-else 
                    class="table-item__key"
                    :class="{
                        'table-item__key--warning': item.signs_num === 3,
                        'table-item__key--alert': item.signs_num === 2,
                        'table-item__key--action': item.signs_num === 1
                    }"
                >
                    {{ item.idx }}
                </div>
                <div 
                    class="table-item__value"
                    :class="{
                        'table-item__value--not-alert': `${item.idx}` === focal_sign_number && item.signs_num === '-',
                        'table-item__value--warning': `${item.idx}` === focal_sign_number && item.signs_num === 3,
                        'table-item__value--alert': `${item.idx}` === focal_sign_number && item.signs_num === 2,
                        'table-item__value--action': `${item.idx}` === focal_sign_number && item.signs_num === 1,
                    }"
                > 
                    <template v-if="!is_show_raw_html">
                        <v-icon v-if="[1, 2, 3].includes(item['signs_num'])" 
                            :class="{
                                'signal--warning': item['signs_num'] === 3,
                                'signal--alert': item['signs_num'] === 2,
                                'signal--action': item['signs_num'] === 1,
                                'signal--map-inactive': !is_monitor_open_emap
                            }"
                            @click="focus_on_sign(item.idx)"
                        >
                            {{ monitor_signs_icon_name_mapping[item['signs_num']] }}
                        </v-icon>
                        <span v-else
                            :class="{
                                'signal--map-active': is_monitor_open_emap
                            }"
                            @click="focus_on_sign(item.idx)"
                        >
                            {{ item['signs_num'] }}
                        </span>
                    </template>
                    <template v-else>
                        <span v-html="item['signs_num']" />
                    </template>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
    import { monitor_signs_icon_name_mapping } from "@/config/monitor_config.js"
    import { mapState, mapMutations } from 'vuex'
    export default {
        name: 'MonitorSigns',
        
        props: {
            data: {
                type: Array,
                default () {
                    return [{'idx':1}, {'idx':2}, {'idx':3}]
                }
            },
            sort_type: {
                type: String,
                default() {
                    return 'int_case'
                }
            },
            enable_display_tooltip: {
                type: Boolean,
                default() {
                    return false
                }
            },
            generate_tooltop_display_info: {
                type: Function,
                default() {
                    return () => {}
                }
            },
            is_show_raw_html: {
                type: Boolean,
                default() {
                    return false
                }
            },
            hide_alert_remarks: {
                type: Boolean,
                default() {
                    return false
                }
            },
            hide_explanation_remarks: {
                type: Boolean,
                default() {
                    return false
                }
            }
        },

        data() {
            return{ 
                monitor_signs_icon_name_mapping: monitor_signs_icon_name_mapping,

                basic_alert_signs: [
                    {text: '預警值', num: '3', css: 'signal-warning'},
                    {text: '警戒值', num: '2', css: 'signal-alert'},
                    {text: '行動值', num: '1', css: 'signal-action'},
                ] 
            }
        },
        
        computed: {
            ...mapState('emap-monitor', [
                'is_monitor_open_emap',
                'focal_sign_number'
            ]),

            sort_data() {
                return this.sort_func();
            },
        },

        beforeDestroy() {
            this.set_focal_sign_number('')
        },

        methods: {
            ...mapMutations('emap-monitor', [
                'set_focal_sign_number',
            ]),

            sort_func() {
                if(this.sort_type === 'int_case') {
                    return this.sort_by_int_case()
                } 
                
                if (this.sort_type === 'city_case') {
                    return this.sort_by_city_case()
                }

                return this.data
            },

            sort_by_int_case() {
                return [...this.data]
                    .sort(this._sorter_by_signs_num())
                    .filter(this._filter_first_item_for_same_idx())
                    .sort((a, b) => a.idx - b.idx)
            },

            sort_by_city_case() {
                const CITY_ORDERS = ["基隆市", "臺北市", "新北市", "桃園市", "新竹縣", "苗栗縣", "臺中市", "南投縣", "彰化縣", "雲林縣", "嘉義縣", "臺南市", "高雄市", "屏東縣", "宜蘭縣", "花蓮縣", "臺東縣"];
                return [...this.data]
                    .sort(this._sorter_by_signs_num())
                    .filter(this._filter_first_item_for_same_idx())
                    .sort((a, b) => CITY_ORDERS.findIndex(city => city === a.idx) - CITY_ORDERS.findIndex(city => city === b.idx))
            },

            _sorter_by_signs_num() {
                return (a, b) => {
                    if (a.signs_num !== "-" && b.signs_num !== "-") return a.signs_num - b.signs_num
                    if (a.signs_num !== "-" && b.signs_num === "-") return -1 
                    if (a.signs_num === "-" && b.signs_num !== "-") return 1
                }
            },

            _filter_first_item_for_same_idx() {
                return (value, index, self) => {
                    const first_same_idx_item_index = self.findIndex(item => item.idx === value.idx)
                    return first_same_idx_item_index === index
                }
            },

            focus_on_sign(item_num) {
                this.set_focal_sign_number(item_num)
            }
        },
    }
</script>

<style lang="scss" scoped>
    @import '../../../../css/mixin';
    @import '../../../../css/monitor/sign';

    .monitor-signs {
        margin: .5rem 1rem 0;
        font-size: 1.1rem;

        .table {
            display: grid;
            grid-template-columns: repeat(10, 1fr);

            @include pad {
                grid-template-columns: repeat(8, 1fr);
            }

            @include mobile {
                grid-template-columns: repeat(4, 1fr);
            }

            &-item {
                display: grid;
                grid-template-columns: 1fr 1fr;
                border-bottom: .1rem solid #fff;
                font-weight: bold;

                &__key {
                    padding: 2px 5px;
                    letter-spacing: .8px;
                    line-height: 1.5;
                    text-align: center;
                    color: var(--base_black);
                    background: var(--monitor_header);

                    // 五邊形
                    &--warning {
                        background: var(--base-yellow);
                    }

                    // 菱形
                    &--alert {
                        background: var(--base-orange);
                    }

                    // 三角形
                    &--action {
                        background: var(--base-red);
                    }
                }

                &__value {
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 2px 5px;

                    // 五邊形
                    &--warning {
                        background: rgba(241, 217, 74, .24);
                    }

                    // 菱形
                    &--alert {
                        background: rgba(237, 161, 47, .24);
                    }

                    // 三角形
                    &--action {
                        background: rgba(238, 57, 52, .24);
                    }

                    &--not-alert {
                        background: var(--white-bg);
                    }
                }
            }
        }
    }

    .signal--map-active {
        cursor: pointer !important;
    }

    .signal--map-inactive {
        cursor: default !important;
    }
</style>

<style scoped>
    .signs-description {
        display: inline-block;
        padding: 5px 10px;
    }

    .links:hover {
        cursor: pointer;
    }

    span >>> .dot {
        display: inline-block;
        width: 1em;
        height: 1em;
        border-radius: 50%;
    }
</style>